import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Check,
    X,
    Pause,
    Play,
    Trash2,
    Eye,
    MousePointerClick,
    Clock,
    ExternalLink,
} from "lucide-react";
import { api, errorMessage, publicAssetUrl } from "@/lib/api";
import {
    listAdminVideos,
    approveVideo,
    rejectVideo,
    pauseVideo,
    resumeVideo,
    deleteAdminVideo,
    VIDEO_STATUS_TONE,
    humanBytes,
    formatDur,
} from "@/lib/ads";
import { formatDate } from "@/lib/format";

const STATUS_TABS = ["PENDING_REVIEW", "LIVE", "APPROVED", "REJECTED", "PAUSED", "EXPIRED"];

export default function VideoQueuePage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("PENDING_REVIEW");
    const [districts, setDistricts] = useState([]);
    const [districtFilter, setDistrictFilter] = useState("");
    const [pendingCount, setPendingCount] = useState(0);
    const [rejecting, setRejecting] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const q = { status: tab, limit: 200 };
            if (districtFilter) q.district_slug = districtFilter;
            const items = await listAdminVideos(q);
            setRows(items);
            // Keep the header badge in sync — if we're already on the PENDING tab, use `items`;
            // otherwise fetch the pending list separately.
            if (tab === "PENDING_REVIEW" && !districtFilter) {
                setPendingCount(items.length);
            } else {
                const pending = await listAdminVideos({ status: "PENDING_REVIEW", limit: 200 });
                setPendingCount(pending.length);
            }
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {});
    }, []);
    useEffect(() => {
        load();
    }, [tab, districtFilter]); // eslint-disable-line

    const onApprove = async (row) => {
        try {
            await approveVideo(row.id);
            toast.success(`Approved · status now ${row.start_date <= new Date().toISOString().slice(0, 10) ? "LIVE" : "APPROVED"}`);
            load();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    const onPause = async (row) => {
        try {
            await pauseVideo(row.id);
            toast.success("Paused");
            load();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    const onResume = async (row) => {
        try {
            await resumeVideo(row.id);
            toast.success("Resumed");
            load();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    const onDelete = async (row) => {
        if (!window.confirm("Delete this video ad?")) return;
        try {
            await deleteAdminVideo(row.id);
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    return (
        <div data-testid="video-queue-page">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-sm font-medium text-orange-600">Monetization · Videos</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-neutral-900">Video Review Queue</h1>
                    <p className="mt-1 text-sm text-neutral-500">Approve or reject merchant-submitted video ads.</p>
                </div>
                <Badge className="rounded-full bg-rose-600 px-3 py-1 text-sm font-bold text-white" data-testid="pending-review-count">
                    {pendingCount} pending
                </Badge>
            </div>

            {/* Status tabs */}
            <div className="mb-4 flex flex-wrap gap-2">
                {STATUS_TABS.map((t) => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                            tab === t
                                ? "border-orange-500 bg-orange-500 text-white"
                                : "border-neutral-300 bg-white text-neutral-700 hover:border-orange-400"
                        }`}
                        data-testid={`video-tab-${t}`}
                    >
                        {t.replace("_", " ")}
                    </button>
                ))}
                <div className="ml-auto">
                    <Select
                        value={districtFilter || "__all__"}
                        onValueChange={(v) => setDistrictFilter(v === "__all__" ? "" : v)}
                    >
                        <SelectTrigger className="w-40 text-xs" data-testid="video-district-filter">
                            <SelectValue placeholder="All districts" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">All districts</SelectItem>
                            {districts.map((d) => (
                                <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loading ? (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-72 rounded-2xl" />
                    ))}
                </div>
            ) : rows.length === 0 ? (
                <Card className="rounded-2xl border border-neutral-200 p-16 text-center">
                    <div className="text-sm font-semibold text-neutral-700">No {tab.replace("_", " ")} videos</div>
                    <div className="mt-1 text-xs text-neutral-500">You're all caught up.</div>
                </Card>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {rows.map((r) => (
                        <VideoCard
                            key={r.id}
                            row={r}
                            onApprove={onApprove}
                            onPause={onPause}
                            onResume={onResume}
                            onDelete={onDelete}
                            onRejectOpen={() => setRejecting(r)}
                        />
                    ))}
                </div>
            )}

            <RejectDialog
                row={rejecting}
                onOpenChange={(v) => !v && setRejecting(null)}
                onDone={() => {
                    setRejecting(null);
                    load();
                }}
            />
        </div>
    );
}

function VideoCard({ row, onApprove, onPause, onResume, onDelete, onRejectOpen }) {
    const isLive = row.status === "LIVE";
    const isPending = row.status === "PENDING_REVIEW";
    const isPaused = row.status === "PAUSED";
    const isRejected = row.status === "REJECTED";
    const ctr = row.impressions_count ? ((row.clicks_count / row.impressions_count) * 100).toFixed(1) : "0.0";

    return (
        <Card className="overflow-hidden rounded-2xl border border-neutral-200" data-testid={`video-card-${row.id}`}>
            <div className="relative bg-neutral-900">
                <video
                    src={publicAssetUrl(row.video_url)}
                    poster={row.thumbnail_url ? publicAssetUrl(row.thumbnail_url) : undefined}
                    controls
                    muted
                    preload="metadata"
                    className="aspect-video w-full bg-black"
                    data-testid={`video-player-${row.id}`}
                />
                <Badge
                    variant="outline"
                    className={`absolute top-2 right-2 text-[10px] uppercase ${VIDEO_STATUS_TONE[row.status] || ""}`}
                    data-testid={`video-status-${row.id}`}
                >
                    {row.status.replace("_", " ")}
                </Badge>
            </div>
            <div className="p-3">
                <div className="text-sm font-bold text-neutral-900">{row.title}</div>
                <div className="mt-0.5 text-xs text-neutral-500">
                    {row.merchant_business_name || row.merchant_user_id?.slice(0, 8)}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                    <Badge variant="outline" className="capitalize">{row.district_slug}</Badge>
                    <Badge variant="outline" className="capitalize">{row.category_slug}</Badge>
                    <Badge variant="outline"><Clock size={9} className="mr-1" /> {formatDur(row.duration_seconds)}</Badge>
                    <Badge variant="outline">{humanBytes(row.file_size_bytes)}</Badge>
                </div>
                <div className="mt-2 text-[11px] text-neutral-500">
                    {formatDate(row.start_date)} → {formatDate(row.end_date)}
                </div>
                {isLive && (
                    <div className="mt-2 flex items-center gap-3 text-[11px] text-neutral-600">
                        <span className="inline-flex items-center gap-1"><Eye size={11} /> {row.impressions_count}</span>
                        <span className="inline-flex items-center gap-1"><MousePointerClick size={11} /> {row.clicks_count}</span>
                        <span className="font-semibold">CTR {ctr}%</span>
                    </div>
                )}
                {row.rejection_reason && (
                    <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-800">
                        <b>Rejected:</b> {row.rejection_reason}
                    </div>
                )}
                {row.linked_post_id && (
                    <Link to={`/posts/${row.linked_post_id}`} target="_blank" rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-orange-600 hover:text-orange-700">
                        View linked offer <ExternalLink size={10} />
                    </Link>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                    {isPending && (
                        <>
                            <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => onApprove(row)} data-testid={`video-approve-${row.id}`}>
                                <Check size={14} className="mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" onClick={onRejectOpen} data-testid={`video-reject-${row.id}`}>
                                <X size={14} className="mr-1" /> Reject
                            </Button>
                        </>
                    )}
                    {isLive && (
                        <Button size="sm" variant="outline" onClick={() => onPause(row)} data-testid={`video-pause-${row.id}`}>
                            <Pause size={14} className="mr-1" /> Pause
                        </Button>
                    )}
                    {isPaused && (
                        <Button size="sm" variant="outline" onClick={() => onResume(row)} data-testid={`video-resume-${row.id}`}>
                            <Play size={14} className="mr-1" /> Resume
                        </Button>
                    )}
                    {isRejected && (
                        <Button size="sm" variant="outline" className="text-rose-600" onClick={() => onDelete(row)} data-testid={`video-delete-${row.id}`}>
                            <Trash2 size={14} className="mr-1" /> Delete
                        </Button>
                    )}
                </div>
            </div>
        </Card>
    );
}

function RejectDialog({ row, onOpenChange, onDone }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (row) setReason("");
    }, [row]);

    const submit = async () => {
        if (!reason.trim()) return toast.error("Please enter a reason");
        try {
            setBusy(true);
            await rejectVideo(row.id, reason.trim());
            toast.success("Rejected");
            onDone?.();
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!row} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="video-reject-dialog">
                <DialogHeader>
                    <DialogTitle>Reject "{row?.title}"?</DialogTitle>
                    <DialogDescription>
                        The merchant will see this reason on their video ad card.
                    </DialogDescription>
                </DialogHeader>
                <div>
                    <Label>Reason *</Label>
                    <Textarea
                        rows={4}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Poor quality, incorrect duration, misleading content…"
                        data-testid="video-reject-reason-input"
                    />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={busy}
                        className="bg-rose-600 text-white hover:bg-rose-700"
                        data-testid="video-reject-submit"
                    >
                        {busy ? "Rejecting…" : "Reject"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
